import { motion } from 'framer-motion';
import React from 'react';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { fontFamily, fontSize } from '../../../constants/text';
import { smallerThan } from '../../../core/util/styledComponents';
import { HomepageSearchType } from '../../../routes/home/Homepage';
import HomepageSearch from '../../UniversalSearch/Homepage';
import Background from './Background';
import Tabs from './Tabs';

const Root = styled(motion.div)`
  position: relative;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  overflow: hidden;
`;

const SearchContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: stretch;
  padding: 60px 0;
  z-index: 10;
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

  > *:not(:last-child) {
    margin-bottom: 25px;
  }
`;

const TabsContainer = styled.div`
  align-self: center;
  max-width: 100%;
`;

const UniversalSearchContainer = styled.div`
  padding: 0 10px;
`;

const TextContainer = styled(motion.div)`
  text-align: center;
  padding: 0 20px;
  max-width: 1000px;
  margin: 0 auto;
  z-index: 10;
  padding-bottom: 30px;
`;

const Header = styled.h1`
  margin: 0;
  padding: 0;
  font-size: ${fontSize.wowThatsHuge};
  font-family: ${fontFamily.bold};
  color: black;
  text-align: center;
  margin-bottom: 15px;
  z-index: 10;

  ${smallerThan.lg`
    font-size: ${fontSize.hellzaLarge};
  `}

  ${smallerThan.md`
    font-size: ${fontSize.hellaLarge};
  `}

  ${smallerThan.sm`
    font-size: ${fontSize.quiteExtraExtraLarge};
  `}
`;
const Subtitle = styled.p`
  margin: 0;
  padding: 0;
  font-size: ${fontSize.quiteExtraLarge};
  font-family: ${fontFamily.book};
  color: black;
  text-align: center;
  z-index: 10;

  ${smallerThan.lg`
    font-size: ${fontSize.large};
  `}

  ${smallerThan.lg`
    font-size: ${fontSize.medium};
  `}
`;

type Props = {
  currentSearchType: HomepageSearchType;
  setCurrentSearchType: (t: HomepageSearchType) => void;
};

/**
 * The hero section of the homepage, which give users a place to search for various kinds of bookings.
 */
const Hero = ({ currentSearchType, setCurrentSearchType }: Props) => {
  return (
    <Root>
      <SearchContainer>
        <TabsContainer>
          <Tabs
            onChange={(selected) => {
              setCurrentSearchType(selected as HomepageSearchType);
            }}
            selectedTab={currentSearchType}
            tabs={[
              { title: 'In Person Care', value: 'inPerson', compactTitle: 'In Person' },
              { title: 'Video Visit', value: 'video', compactTitle: 'Video' },
              { title: 'COVID-19 Test', value: 'covid', compactTitle: 'COVID Test' },
              { title: 'COVID-19 Vaccine', value: 'vaccine', compactTitle: 'Vaccine' },
            ]}
          />
        </TabsContainer>
        <UniversalSearchContainer>
          <HomepageSearch type={currentSearchType} />
        </UniversalSearchContainer>
      </SearchContainer>
      <div>
        <TextContainer layout>
          <Header>Hassle free healthcare</Header>
          <Subtitle>Book same-day appointments for you and your kids.</Subtitle>
        </TextContainer>
        <Background type={currentSearchType} />
      </div>
    </Root>
  );
};

export default Hero;
